import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import {
  combineLatest,
  debounceTime,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  withLatestFrom,
} from 'rxjs';
import { faqResponse, HelpArticle } from 'src/app/services/models/faq.model';
import { LoadingSpinnerComponent } from '../custom/loading-spinner/loading-spinner.component';
import { MatExpansionModule } from '@angular/material/expansion';
import { SafePipe } from 'src/app/pipes/safe.pipe';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FaqsService } from 'src/app/services/faqs.service';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { MatIconModule } from '@angular/material/icon';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { animate, style, transition, trigger } from '@angular/animations';
import { MatTabsModule } from '@angular/material/tabs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-faq-page',
  imports: [
    CommonModule,
    LoadingSpinnerComponent,
    RouterModule,
    MatExpansionModule,
    SafePipe,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    MatTabsModule,
  ],
  templateUrl: './faq-page.component.html',
  styleUrl: './faq-page.component.css',
  animations: [
    trigger('heightAnimation', [
      transition(':enter', [
        style({ height: 0, opacity: 0 }),
        animate('300ms ease-out', style({ height: '*', opacity: 1 })),
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1 }),
        animate('300ms ease-out', style({ height: 0, opacity: 0 })),
      ]),
    ]),
  ],
})
export class FaqPageComponent implements OnInit {
  destoryRef = inject(DestroyRef);
  routerService = inject(ActivatedRoute);
  router = inject(Router);
  faqsService = inject(FaqsService);
  breakpointObserver = inject(BreakpointObserver);
  serviceData$: Observable<faqResponse>;
  searchFilterControl = new FormControl('');
  isLoading = signal(true);
  faqsData$: Observable<{ title: string; articles: HelpArticle[] }[]>;
  articleData = signal<HelpArticle>(null);

  uiState = signal<{
    isMobile: boolean;
    routeHasParams: boolean;
    hideArticle: boolean;
    isNavHidden: boolean;
  }>({
    isMobile: false,
    routeHasParams: false,
    hideArticle: false,
    isNavHidden: false,
  });

  ngOnInit(): void {
    this.serviceData$ = this.initializeServiceData();

    this.routerService.parent?.url
      .pipe(takeUntilDestroyed(this.destoryRef))
      .subscribe((segments) => {
        const parent = segments.map((segment) => segment.path).join('/');
        this.uiState.update((val) => ({
          ...val,
          isNavHidden: parent !== 'app',
        }));
      });

    /**
      handles case where user moves from mobile to desktop and url doesn't contain group & article
      Need to update url with group/article to show data in desktop, otherwise shows blank page
    */
    combineLatest([
      this.breakpointObserver.observe([Breakpoints.HandsetPortrait]),
      this.serviceData$,
    ])
      .pipe(
        withLatestFrom(this.routerService.paramMap),
        map(([[pointbreakObserver, serviceData], routeParams]) => {
          if (
            !pointbreakObserver.matches &&
            !routeParams.has('group') &&
            serviceData != null
          ) {
            const faqGroup = serviceData.faqs[0];
            this.router.navigate(
              [faqGroup.handle, faqGroup.articles[0].handle],
              { relativeTo: this.routerService }
            );
            this.uiState.update((val) => ({ ...val, hideArticle: false }));
          }
          this.uiState.update((val) => ({
            ...val,
            isMobile: pointbreakObserver.matches,
          }));
        }),
        takeUntilDestroyed(this.destoryRef)
      )
      .subscribe();

    // Process URL parameters and create grouped data
    this.faqsData$ = combineLatest([
      this.routerService.paramMap,
      this.serviceData$,
      this.searchFilterControl.valueChanges.pipe(
        startWith(''),
        debounceTime(300)
      ),
    ]).pipe(
      map(([paramMap, serviceData, filterVal]) => {
        const grouped = new Map<
          string,
          { title: string; handle: string; articles: HelpArticle[] }
        >();

        // Group the FAQ data
        serviceData.faqs.forEach((faqGroup) => {
          if (!grouped.has(faqGroup.handle)) {
            const filteredArticles = faqGroup.articles.filter((article) => {
              if (!filterVal) return true;
              const searchTerm = filterVal.toLowerCase();
              return (
                article.title.toLowerCase().includes(searchTerm) ||
                article.handle.toLowerCase().includes(searchTerm)
              );
            });
            if (filteredArticles.length > 0) {
              grouped.set(faqGroup.handle, {
                title: faqGroup.title,
                handle: faqGroup.handle,
                articles: filteredArticles,
              });
            }
          }
        });

        const groupedList = Array.from(grouped.values());
        // Handle article selection based on URL
        if (paramMap.has('group')) {
          // Get route parameters
          const routeGroup = paramMap.get('group');
          const routeArticle = paramMap.get('article');
          const articlesInGroup = grouped.get(routeGroup);
          if (articlesInGroup) {
            const setArticle = articlesInGroup.articles.find(
              (a) => a.handle === routeArticle
            );
            if (setArticle) {
              this.clickArticle(setArticle);
            }
          }
        } else {
          // Set default article if no specific one is selected
          this.clickArticle(groupedList[0]?.articles[0]);
          this.uiState.update((val) => {
            return { ...val, hideArticle: true };
          });
        }
        this.uiState.update((val) => ({
          ...val,
          routeHasParams: paramMap.has('group'),
        }));

        this.isLoading.set(false);
        return groupedList;
      }),
      takeUntilDestroyed(this.destoryRef)
    );
  }

  private initializeServiceData(): Observable<faqResponse> {
    // If data exists in service, use it
    if (this.faqsService.faqs) {
      return of(this.faqsService.faqs);
    }
    // Otherwise fetch and cache in service
    return this.faqsService.fetchFaqs().pipe(map((response) => response));
  }

  clickArticle(article: HelpArticle) {
    this.articleData.set(article);
  }
}
